import { forwardRef, useContext, useState } from 'react'
import { ThemeContext } from '@app/ThemeProvider'
import ReactECharts from 'echarts-for-react'
import type {
  PropertiesMetricOption,
  RiskProfilePropertiesResponse,
} from '../types/riskProfileProperties.types'
import { formatRiskProfileValue } from '../utils/riskProfile.utils'
import { mapRiskProfileSocialEconomicResponseToRanking } from './RiskProfileSocialEconomicChartData'

export interface EconomicHazardChartProps {
  data: RiskProfilePropertiesResponse
  metricOption: PropertiesMetricOption
  entityNoun: string
}

const TOP_N_HAZARDS = 10
const FALLBACK_BAR_COLOUR = '#3BB5CA'

const EconomicHazardChart = forwardRef<ReactECharts, EconomicHazardChartProps>(
  ({ data, metricOption, entityNoun }, ref) => {
    const { isDark } = useContext(ThemeContext)
    const [barColour] = useState(() => {
      const resolved = getComputedStyle(document.documentElement)
        .getPropertyValue('--chart-primary')
        .trim()
      return resolved ? `hsl(${resolved})` : FALLBACK_BAR_COLOUR
    })

    const chartData = mapRiskProfileSocialEconomicResponseToRanking(data, {
      metricOption,
      topN: TOP_N_HAZARDS,
    })

    if (chartData.labels.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">No comparable hazards for this selection.</p>
      )
    }

    const percentOption: PropertiesMetricOption = { ...metricOption, type: 'percentage' }
    const totalOption: PropertiesMetricOption = { ...metricOption, type: 'total' }

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'none',
        },
        valueFormatter: (value: number) => formatRiskProfileValue(value, totalOption),
      },

      grid: {
        left: 0,
        right: 140,
        top: 32,
        bottom: 32,
        containLabel: false,
      },

      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => formatRiskProfileValue(value, totalOption),
          color: isDark ? '#fff' : '#000',
          show: false,
        },
        splitNumber: 4,
        splitLine: { show: false },
      },

      yAxis: {
        type: 'category',
        data: chartData.labels,
        inverse: true,
        axisLabel: {
          align: 'left',
          width: 160,
          margin: 160,
          overflow: 'truncate',
          color: isDark ? '#fff' : '#000',
        },
      },

      series: [
        {
          type: 'bar',
          data: chartData.values,
          barMaxWidth: 18,

          itemStyle: {
            color: barColour,
          },

          emphasis: {
            disabled: true,
          },

          label: {
            show: true,
            position: 'right',
            formatter: (params: { dataIndex: number }) => {
              const total = chartData.totals[params.dataIndex]

              const percentText = formatRiskProfileValue(total.percentValue ?? 0, percentOption)

              const countText = formatRiskProfileValue(total.value, totalOption, total.unit)

              return `${percentText}  ${countText} ${entityNoun}`
            },
            color: isDark ? '#fff' : '#000',
            fontWeight: 'bold',
          },
        },
      ],
    }

    return (
      <ReactECharts
        ref={ref}
        option={option}
        notMerge
        style={{ height: Math.max(280, chartData.labels.length * 36 + 80) }}
      />
    )
  },
)

EconomicHazardChart.displayName = 'EconomicHazardChart'

export default EconomicHazardChart
